// Categorias.js - VERSÃO COM 4 COLUNAS E 12 CARDS POR PÁGINA

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Categorias.css';
import { Icon } from './SharedComponents';

// 🔥 BASE URL
const API_BASE_URL = '/api';

export default function Categorias() {
    // ==============================================
    // ESTADOS PRINCIPAIS
    // ==============================================
    const [categorias, setCategorias] = useState([]);
    const [filteredCategorias, setFilteredCategorias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [configuracao, setConfiguracao] = useState(null);

    // ==============================================
    // MODAIS
    // ==============================================
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [categoriaToDelete, setCategoriaToDelete] = useState(null);
    const [categoriaEdit, setCategoriaEdit] = useState(null);

    // ==============================================
    // FORM DATA
    // ==============================================
    const [formData, setFormData] = useState({
        nome: '',
        descricao: '',
        cor: '#6C63FF',
        icone: '📁'
    });

    // ==============================================
    // 🔥 PAGINAÇÃO - 12 CARDS POR PÁGINA
    // ==============================================
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 9, // 🔥 ALTERADO PARA 12
        total: 0,
        pages: 0
    });

    // ==============================================
    // ESTADOS ADICIONAIS
    // ==============================================
    const [message, setMessage] = useState({ text: '', type: '' });
    const [submitting, setSubmitting] = useState(false);
    const [loggedUser, setLoggedUser] = useState(null);
    const [empresaId, setEmpresaId] = useState(null);
    const [isSuperadmin, setIsSuperadmin] = useState(false);

    // ==============================================
    // 🔥 API INSTANCE
    // ==============================================
    const api = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache',
            'Content-Type': 'application/json'
        }
    });

    // ==============================================
    // INTERCEPTORES PARA DEBUG
    // ==============================================
    api.interceptors.request.use(
        config => {
            console.log('📤 [REQUEST]', config.method.toUpperCase(), config.baseURL + config.url);
            return config;
        },
        error => {
            console.error('❌ [REQUEST ERROR]', error);
            return Promise.reject(error);
        }
    );

    api.interceptors.response.use(
        response => {
            console.log('📥 [RESPONSE]', response.status, response.config.url);
            return response;
        },
        error => {
            console.error('❌ [RESPONSE ERROR]', error.response?.status, error.response?.config?.url);
            console.error('  - Data:', error.response?.data);
            return Promise.reject(error);
        }
    );

    // ==============================================
    // FUNÇÕES AUXILIARES
    // ==============================================
    const extrairEmpresaId = (empresa) => {
        if (!empresa) return null;
        if (typeof empresa === 'string') return empresa;
        if (typeof empresa === 'object' && empresa._id) return empresa._id;
        if (typeof empresa === 'object' && empresa.id) return empresa.id;
        return null;
    };

    const obterEmpresaIdDoUtilizador = (user) => {
        if (!user) return null;
        let id = extrairEmpresaId(user.empresaId);
        if (id) return id;
        id = extrairEmpresaId(user.empresa);
        if (id) return id;
        id = extrairEmpresaId(user.empresaAtual);
        if (id) return id;
        if (user.empresas && Array.isArray(user.empresas) && user.empresas.length > 0) {
            id = extrairEmpresaId(user.empresas[0]);
            if (id) return id;
        }
        return null;
    };

    const showToast = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    };

    // ==============================================
    // FETCH LOGGED USER
    // ==============================================
    const fetchLoggedUser = async () => {
        console.log('🔍 [1] Buscando usuário logado...');
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
                if (storedUser && storedUser._id) {
                    setLoggedUser(storedUser);
                    setIsSuperadmin(storedUser.perfil === 'Superadmin');
                    const empresaIdExtraido = obterEmpresaIdDoUtilizador(storedUser);
                    setEmpresaId(empresaIdExtraido);
                    return storedUser;
                }
                return null;
            }

            const response = await axios.get(`${API_BASE_URL}/auth/users/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                const userData = response.data.data;
                setLoggedUser(userData);
                setIsSuperadmin(userData.perfil === 'Superadmin');
                const empresaIdExtraido = obterEmpresaIdDoUtilizador(userData);
                setEmpresaId(empresaIdExtraido);
                localStorage.setItem('usuario', JSON.stringify(userData));
                return userData;
            }
        } catch (err) {
            console.error('❌ Erro ao buscar utilizador:', err);
            const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
            if (storedUser && storedUser._id) {
                setLoggedUser(storedUser);
                setIsSuperadmin(storedUser.perfil === 'Superadmin');
                const empresaIdExtraido = obterEmpresaIdDoUtilizador(storedUser);
                setEmpresaId(empresaIdExtraido);
                return storedUser;
            }
        }
        return null;
    };

    // ==============================================
    // FETCH CATEGORIAS
    // ==============================================
    const fetchCategorias = async () => {
        console.log('📦 [2] Buscando categorias...');
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setLoading(false);
                return;
            }

            const response = await api.get('/categorias');

            let categoriasData = [];
            let configData = null;

            if (response.data) {
                if (response.data.data && Array.isArray(response.data.data)) {
                    categoriasData = response.data.data;
                    if (response.data.configuracao) {
                        configData = response.data.configuracao;
                        setConfiguracao(configData);
                    }
                } else if (Array.isArray(response.data)) {
                    categoriasData = response.data;
                }
            }

            console.log(`  ✅ ${categoriasData.length} categorias carregadas`);
            setCategorias(categoriasData);
            setFilteredCategorias(categoriasData);

            // 🔥 CALCULAR PÁGINAS COM LIMIT 9
            setPagination(prev => ({
                ...prev,
                total: categoriasData.length,
                pages: Math.ceil(categoriasData.length / 9), // 🔥 9 por página
                page: 1
            }));

        } catch (err) {
            console.error('❌ Erro ao carregar categorias:', err);
            showToast(err.response?.data?.erro || 'Erro ao carregar categorias', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // ==============================================
    // FILTRAR CATEGORIAS
    // ==============================================
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredCategorias(categorias);
            setPagination(prev => ({
                ...prev,
                total: categorias.length,
                pages: Math.ceil(categorias.length / 9), // 🔥 9 por página
                page: 1
            }));
        } else {
            const term = searchTerm.toLowerCase().trim();
            const filtered = categorias.filter(c =>
                c.nome.toLowerCase().includes(term) ||
                (c.descricao && c.descricao.toLowerCase().includes(term))
            );
            setFilteredCategorias(filtered);
            setPagination(prev => ({
                ...prev,
                total: filtered.length,
                pages: Math.ceil(filtered.length / 9), // 🔥 9 por página
                page: 1
            }));
        }
    }, [searchTerm, categorias]);

    // ==============================================
    // HANDLE CHANGE
    // ==============================================
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // ==============================================
    // RESET FORM
    // ==============================================
    const resetForm = () => {
        setFormData({
            nome: '',
            descricao: '',
            cor: '#6C63FF',
            icone: '📁'
        });
        setCategoriaEdit(null);
    };

    // ==============================================
    // CRIAÇÃO
    // ==============================================
    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        if (!formData.nome.trim()) {
            showToast('O nome da categoria é obrigatório', 'danger');
            setSubmitting(false);
            return;
        }

        let empresaParaEnviar = empresaId;
        if (!empresaParaEnviar && loggedUser) {
            empresaParaEnviar = obterEmpresaIdDoUtilizador(loggedUser);
        }

        if (!empresaParaEnviar) {
            showToast('Utilizador sem empresa associada. Contacte o administrador.', 'danger');
            setSubmitting(false);
            return;
        }

        try {
            const dadosEnvio = {
                nome: formData.nome.trim(),
                descricao: formData.descricao?.trim() || '',
                cor: formData.cor || '#6C63FF',
                icone: formData.icone || '📁',
                empresaId: empresaParaEnviar
            };

            const response = await api.post('/categorias', dadosEnvio);

            if (response.data) {
                showToast('Categoria criada com sucesso!', 'success');
                resetForm();
                setShowCreateModal(false);
                await fetchCategorias();
            }
        } catch (err) {
            console.error('❌ Erro ao criar categoria:', err);
            const erroMsg = err.response?.data?.erro ||
                err.response?.data?.mensagem ||
                err.message ||
                'Erro ao criar categoria';
            showToast(erroMsg, 'danger');
        } finally {
            setSubmitting(false);
        }
    };

    // ==============================================
    // EDIÇÃO
    // ==============================================
    const handleEdit = (categoria) => {
        setCategoriaEdit(categoria);
        setFormData({
            nome: categoria.nome || '',
            descricao: categoria.descricao || '',
            cor: categoria.cor || '#6C63FF',
            icone: categoria.icone || '📁'
        });
        setShowEditModal(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        if (!formData.nome.trim()) {
            showToast('O nome da categoria é obrigatório', 'danger');
            setSubmitting(false);
            return;
        }

        try {
            const dadosEnvio = {
                nome: formData.nome.trim(),
                descricao: formData.descricao?.trim() || '',
                cor: formData.cor || '#6C63FF',
                icone: formData.icone || '📁'
            };

            const response = await api.put(`/categorias/${categoriaEdit._id}`, dadosEnvio);

            if (response.data) {
                showToast('Categoria atualizada com sucesso!', 'success');
                resetForm();
                setShowEditModal(false);
                await fetchCategorias();
            }
        } catch (err) {
            console.error('❌ Erro ao atualizar categoria:', err);
            const erroMsg = err.response?.data?.erro ||
                err.response?.data?.mensagem ||
                err.message ||
                'Erro ao atualizar categoria';
            showToast(erroMsg, 'danger');
        } finally {
            setSubmitting(false);
        }
    };

    // ==============================================
    // EXCLUSÃO
    // ==============================================
    const openDeleteModal = (categoria) => {
        setCategoriaToDelete(categoria);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!categoriaToDelete) return;

        try {
            const response = await api.delete(`/categorias/${categoriaToDelete._id}`);

            if (response.data) {
                showToast(`Categoria "${categoriaToDelete.nome}" removida com sucesso!`, 'success');
                setShowDeleteModal(false);
                setCategoriaToDelete(null);
                await fetchCategorias();
            }
        } catch (err) {
            console.error('❌ Erro ao remover categoria:', err);
            const erroMsg = err.response?.data?.erro ||
                err.response?.data?.mensagem ||
                err.message ||
                'Erro ao remover categoria';

            if (err.response?.data?.produtosAssociados) {
                showToast(`${erroMsg} (${err.response.data.produtosAssociados} produtos associados)`, 'danger');
            } else {
                showToast(erroMsg, 'danger');
            }
        }
    };

    // ==============================================
    // PAGINAÇÃO - 9 CARDS POR PÁGINA
    // ==============================================
    const getCurrentPageCategorias = () => {
        const startIndex = (pagination.page - 1) * 9; // 🔥 9 por página
        const endIndex = startIndex + 9; // 🔥 9 por página
        return filteredCategorias.slice(startIndex, endIndex);
    };

    const goToPage = (page) => {
        if (page < 1 || page > pagination.pages) return;
        setPagination(prev => ({ ...prev, page }));
    };

    const renderPagination = () => {
        const { page, pages, total } = pagination;
        if (pages <= 1) return null;

        const startItem = (page - 1) * 9 + 1; // 🔥 9 por página
        const endItem = Math.min(page * 9, total); // 🔥 9 por página

        return (
            <div className="categorias-pagination">
                <div className="pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {total} categorias</span>
                </div>
                <div className="pagination-controls">
                    <button className="pagination-btn" onClick={() => goToPage(page - 1)} disabled={page === 1}>
                        <Icon name="chevron_left" size={18} />
                    </button>
                    <div className="pagination-numbers">
                        {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                            <button
                                key={p}
                                className={`pagination-number ${page === p ? 'active' : ''}`}
                                onClick={() => goToPage(p)}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                    <button className="pagination-btn" onClick={() => goToPage(page + 1)} disabled={page === pages}>
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // ==============================================
    // CARREGAR DADOS INICIAIS
    // ==============================================
    useEffect(() => {
        const carregarDados = async () => {
            console.log('🚀 Iniciando carregamento...');
            await fetchLoggedUser();
            await fetchCategorias();
            console.log('✅ Carregamento concluído!');
        };
        carregarDados();
    }, []);

    // ==============================================
    // RENDER PRINCIPAL
    // ==============================================
    const currentCategorias = getCurrentPageCategorias();

    return (
        <div className="categorias-container">
            {/* MENSAGENS */}
            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    <Icon name={message.type === 'success' ? 'check_circle' : 'error'} size={16} />
                    <span>{message.text}</span>
                    <button className="alert-close" onClick={() => setMessage({ text: '', type: '' })}>×</button>
                </div>
            )}

            {/* MODAL DE CRIAÇÃO */}
{showCreateModal && (
    <div className="categoria-modal-overlay" onClick={() => { setShowCreateModal(false); resetForm(); }}>
        <div className="categoria-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="categoria-modal-header">
                <h3><Icon name="add_box" size={22} /> Nova Categoria</h3>
                <button className="categoria-modal-close" onClick={() => { setShowCreateModal(false); resetForm(); }}>×</button>
            </div>
            <div className="categoria-modal-body">
                <form onSubmit={handleCreateSubmit}>
                    <div className="categoria-form-group">
                        <label>Nome <span className="categoria-required">*</span></label>
                        <input
                            type="text"
                            name="nome"
                            value={formData.nome}
                            onChange={handleChange}
                            placeholder="Nome da categoria"
                            required
                        />
                    </div>

                    <div className="categoria-form-group">
                        <label>Descrição</label>
                        <textarea
                            name="descricao"
                            value={formData.descricao}
                            onChange={handleChange}
                            placeholder="Descrição da categoria (opcional)"
                            rows="2"
                        />
                    </div>

                    <div className="categoria-form-row">
                        <div className="categoria-form-group">
                            <label>Ícone</label>
                            <select
                                name="icone"
                                value={formData.icone}
                                onChange={handleChange}
                            >
                                {ICONES_DISPONIVEIS.map(icon => (
                                    <option key={icon.value} value={icon.value}>
                                        {icon.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="categoria-form-group">
                            <label>Cor</label>
                            <div className="categoria-cor-selector">
                                {CORES_DISPONIVEIS.map(cor => (
                                    <button
                                        key={cor}
                                        type="button"
                                        className={`categoria-cor-btn ${formData.cor === cor ? 'active' : ''}`}
                                        style={{ backgroundColor: cor }}
                                        onClick={() => setFormData(prev => ({ ...prev, cor }))}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="categoria-preview-container">
                        <span>Pré-visualização:</span>
                        <span
                            className="categoria-preview-badge"
                            style={{
                                backgroundColor: formData.cor,
                                color: '#fff',
                                padding: '0.3rem 0.8rem',
                                borderRadius: '100px',
                                fontSize: '0.8rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.3rem'
                            }}
                        >
                            <span>{formData.icone || '📁'}</span>
                            {formData.nome || 'Nome da Categoria'}
                        </span>
                    </div>

                    <div className="categoria-modal-actions">
                        <button type="submit" className="categoria-modal-btn-primary" disabled={submitting}>
                            {submitting ? 'A criar...' : 'Criar Categoria'}
                        </button>
                        <button type="button" className="categoria-modal-btn-secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                            Cancelar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
)}

{/* MODAL DE EDIÇÃO */}
{showEditModal && categoriaEdit && (
    <div className="categoria-modal-overlay" onClick={() => { setShowEditModal(false); resetForm(); }}>
        <div className="categoria-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="categoria-modal-header">
                <h3><Icon name="edit" size={22} /> Editar Categoria</h3>
                <button className="categoria-modal-close" onClick={() => { setShowEditModal(false); resetForm(); }}>×</button>
            </div>
            <div className="categoria-modal-body">
                <form onSubmit={handleEditSubmit}>
                    <div className="categoria-form-group">
                        <label>Nome <span className="categoria-required">*</span></label>
                        <input
                            type="text"
                            name="nome"
                            value={formData.nome}
                            onChange={handleChange}
                            placeholder="Nome da categoria"
                            required
                        />
                    </div>

                    <div className="categoria-form-group">
                        <label>Descrição</label>
                        <textarea
                            name="descricao"
                            value={formData.descricao}
                            onChange={handleChange}
                            placeholder="Descrição da categoria (opcional)"
                            rows="2"
                        />
                    </div>

                    <div className="categoria-form-row">
                        <div className="categoria-form-group">
                            <label>Ícone</label>
                            <select
                                name="icone"
                                value={formData.icone}
                                onChange={handleChange}
                            >
                                {ICONES_DISPONIVEIS.map(icon => (
                                    <option key={icon.value} value={icon.value}>
                                        {icon.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="categoria-form-group">
                            <label>Cor</label>
                            <div className="categoria-cor-selector">
                                {CORES_DISPONIVEIS.map(cor => (
                                    <button
                                        key={cor}
                                        type="button"
                                        className={`categoria-cor-btn ${formData.cor === cor ? 'active' : ''}`}
                                        style={{ backgroundColor: cor }}
                                        onClick={() => setFormData(prev => ({ ...prev, cor }))}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="categoria-preview-container">
                        <span>Pré-visualização:</span>
                        <span
                            className="categoria-preview-badge"
                            style={{
                                backgroundColor: formData.cor,
                                color: '#fff',
                                padding: '0.3rem 0.8rem',
                                borderRadius: '100px',
                                fontSize: '0.8rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.3rem'
                            }}
                        >
                            <span>{formData.icone || '📁'}</span>
                            {formData.nome || 'Nome da Categoria'}
                        </span>
                    </div>

                    <div className="categoria-modal-actions">
                        <button type="submit" className="categoria-modal-btn-primary" disabled={submitting}>
                            {submitting ? 'A atualizar...' : 'Salvar Alterações'}
                        </button>
                        <button type="button" className="categoria-modal-btn-secondary" onClick={() => { setShowEditModal(false); resetForm(); }}>
                            Cancelar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
)}

{/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
{showDeleteModal && categoriaToDelete && (
    <div className="categoria-modal-overlay" onClick={() => setShowDeleteModal(false)}>
        <div className="categoria-modal-content-popups categoria-modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="categoria-modal-header">
                <h3><span className="categoria-header-icon">⚠️</span> Confirmar Exclusão</h3>
                <button className="categoria-modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="categoria-modal-body">
                <div className="categoria-confirm-icon warning">
                    <Icon name="warning" size={32} />
                </div>
                <h4>Remover categoria?</h4>
                <p>Tem certeza que deseja remover a categoria <strong>{categoriaToDelete.nome}</strong>?</p>
                <p className="categoria-confirm-subtext">
                    <Icon name="info" size={14} />
                    Esta ação é irreversível. Produtos com esta categoria perderão a referência.
                </p>
                {categoriaToDelete.contagemProdutos > 0 && (
                    <p className="categoria-confirm-subtext danger">
                        <Icon name="warning" size={14} />
                        <strong>Atenção:</strong> Esta categoria tem {categoriaToDelete.contagemProdutos} produtos associados!
                    </p>
                )}
            </div>
            <div className="categoria-modal-actions">
                <button className="categoria-btn-confirm-danger" onClick={confirmDelete}>
                    <Icon name="delete_forever" size={16} /> Eliminar
                </button>
                <button className="categoria-btn-cancel" onClick={() => setShowDeleteModal(false)}>Cancelar</button>
            </div>
        </div>
    </div>
)}

            {/* HEADER */}
            <div className="categorias-header">
                <div className="header-title">
                    <h2><Icon name="category" size={24} /> Categorias</h2>
                    <p>Gerencie as categorias dos seus produtos</p>
                </div>
                <div className="header-actions">
                    <button
                        className="btn-create"
                        onClick={() => setShowCreateModal(true)}
                    >
                        <Icon name="add" size={18} /> Nova Categoria
                    </button>
                </div>
            </div>

            {/* KPIs */}
            <div className="categorias-kpi-grid">
                <div className="kpi-card primary">
                    <div className="kpi-icon"><Icon name="category" size={16} /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{categorias.length}</span>
                        <span className="kpi-label">Total Categorias</span>
                    </div>
                </div>
                <div className="kpi-card success">
                    <div className="kpi-icon"><Icon name="check_circle" size={16} /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{filteredCategorias.length}</span>
                        <span className="kpi-label">Filtradas</span>
                    </div>
                </div>
                <div className="kpi-card info">
                    <div className="kpi-icon"><Icon name="inventory_2" size={16} /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">
                            {categorias.reduce((sum, c) => sum + (c.contagemProdutos || 0), 0)}
                        </span>
                        <span className="kpi-label">Produtos com Categoria</span>
                    </div>
                </div>
            </div>

            {/* FILTROS */}
            <div className="categorias-filters">
                <div className="search-box">
                    <Icon name="search" size={18} />
                    <input
                        type="text"
                        placeholder="Pesquisar categoria por nome ou descrição..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="search-clear" onClick={() => setSearchTerm('')}>
                            <Icon name="close" size={16} />
                        </button>
                    )}
                </div>
                <button className="btn-refresh" onClick={fetchCategorias} title="Atualizar lista">
                    <Icon name="refresh" size={18} />
                </button>
            </div>

            {/* 🔥 LISTA DE CATEGORIAS - 4 COLUNAS */}
            {loading ? (
                <div className="loading-container">
                    <div className="spinner-large"></div>
                    <p>A carregar categorias...</p>
                </div>
            ) : (
                <>
                    <div className="categorias-grid">
                        {currentCategorias.length === 0 ? (
                            <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
                                <Icon name="category" size={48} />
                                <p>Nenhuma categoria encontrada</p>
                                <button className="btn-create-small" onClick={() => setShowCreateModal(true)}>
                                    Criar primeira categoria
                                </button>
                            </div>
                        ) : (
                            currentCategorias.map(categoria => (
                                <div key={categoria._id} className="categoria-card">
                                    <div className="categoria-card-header">
                                        <div className="categoria-icon" style={{ backgroundColor: categoria.cor || '#6C63FF' }}>
                                            <span style={{ fontSize: '24px' }}>{categoria.icone || '📁'}</span>
                                        </div>
                                        <span className="categoria-nome">{categoria.nome}</span>
                                        <span className="categoria-produtos">
                                            {categoria.contagemProdutos || 0} {categoria.contagemProdutos === 1 ? 'produto' : 'produtos'}
                                        </span>
                                    </div>
                                    {categoria.descricao && (
                                        <div className="categoria-card-body">
                                            <p>{categoria.descricao}</p>
                                        </div>
                                    )}
                                    <div className="categoria-card-actions">
                                        <button
                                            className="btn-edit"
                                            onClick={() => handleEdit(categoria)}
                                            title="Editar"
                                        >
                                            <Icon name="edit" size={16} /> Editar
                                        </button>
                                        <button
                                            className="btn-delete"
                                            onClick={() => openDeleteModal(categoria)}
                                            title="Excluir"
                                            disabled={categoria.contagemProdutos > 0}
                                        >
                                            <Icon name="delete" size={16} />
                                            {categoria.contagemProdutos > 0 ? 'Em uso' : 'Excluir'}
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    {renderPagination()}
                </>
            )}
        </div>
    );
}

// ==============================================
// CONSTANTES (fora do componente)
// ==============================================
const ICONES_DISPONIVEIS = [
    { value: '📁', label: '📁 Pasta' },
    { value: '📂', label: '📂 Pasta Aberta' },
    { value: '🏷️', label: '🏷️ Etiqueta' },
    { value: '📦', label: '📦 Pacote' },
    { value: '🛍️', label: '🛍️ Sacola' },
    { value: '🏪', label: '🏪 Loja' },
    { value: '📋', label: '📋 Lista' },
    { value: '🔖', label: '🔖 Marcador' },
    { value: '⭐', label: '⭐ Estrela' },
    { value: '❤️', label: '❤️ Coração' },
    { value: '🚩', label: '🚩 Bandeira' },
    { value: '📚', label: '📚 Livros' },
    { value: '🔲', label: '🔲 Quadrado' },
    { value: '📱', label: '📱 App' },
    { value: '📊', label: '📊 Gráfico' },
    { value: '🎯', label: '🎯 Alvo' },
    { value: '💼', label: '💼 Pasta de Trabalho' },
    { value: '📎', label: '📎 Clipe' },
    { value: '✏️', label: '✏️ Lápis' },
    { value: '🖊️', label: '🖊️ Caneta' }
];

const CORES_DISPONIVEIS = [
    '#6C63FF', '#7c3aed', '#6d28d9', '#5b21b6',
    '#DC2626', '#EA580C', '#D97706', '#65A30D',
    '#059669', '#0D9488', '#0284C7', '#2563EB',
    '#DB2777', '#EC4899', '#F43F5E', '#E11D48',
    '#BE123C', '#881337', '#1E293B', '#0F172A'
];